// İSG Portal — sayfalama kontrolü (api-contract §3: page/pageSize + filteredTotal).
// Sayaç + önceki/sonraki. Erişilebilir: `nav[aria-label]`, `aria-live` özet.

import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from './cn';

interface PaginationProps {
  page: number;
  pageSize: number;
  /** Filtre sonrası toplam kayıt (sayfalama bunun üzerinden hesaplanır). */
  filteredTotal: number;
  onPageChange: (page: number) => void;
  /** Erişilebilir etiketler (i18n ile). Özet ARTIK burada YOK (S33-8 review fix #2) — bileşen
   * `page`/`pageSize`/`filteredTotal`ten TEK bir kanonik anahtarla (`pagination.summary` /
   * boşsa `pagination.summaryEmpty`) kendi hesaplar; 18+ çağıran kendi `from`/`to` formatını
   * İCAT ETMEZ. Anahtar HER tüketici app'in kendi `locales.ts`'inde tanımlı olmalı (bu paket
   * i18next'i BAŞLATMAZ, yalnız aktif örneği OKUR — bkz. `LanguageSwitcher.tsx`'in aynı deseni). */
  labels: {
    nav: string;
    previous: string;
    next: string;
  };
  /** Daraltılmış görünüm (ör. pano kartı altlığı) — düğmeler ikon-yalnız + küçük olur, nav
   * kendi dikey boşluğunu TAŞIMAZ (dış sarmalayıcı sağlar, bkz. `MissingItemsCard`).
   * Varsayılan (`false`/verilmez) bugünkü render'ı BİREBİR korur. */
  compact?: boolean;
}

export function Pagination({
  page,
  pageSize,
  filteredTotal,
  onPageChange,
  labels,
  compact = false,
}: PaginationProps) {
  const { t, i18n } = useTranslation();
  // Owner review B2 item 6 — boş liste (`filteredTotal === 0`) eskiden yine de "1 / 1" +
  // devre-dışı önceki/sonraki düğmelerini gösteriyordu (`pageCount` alt sınırı 1'de kalır,
  // buttonlar zaten `disabled` olur ama GÖRÜNÜR kalırdı) — sayfalanacak HİÇBİR şey yokken bir
  // sayfalama kontrolü GÖSTERMEK yanıltıcı. Aşağıda `hasRows` yalnız sayaç grubunu (metin +
  // düğmeler) gizler; özet satırı (`summaryEmpty`, "0 kayıt") HER ZAMAN kalır.
  const hasRows = filteredTotal > 0;
  const pageCount = Math.max(1, Math.ceil(filteredTotal / pageSize));
  const current = Math.min(Math.max(1, page), pageCount);
  const summary =
    filteredTotal === 0
      ? t('pagination.summaryEmpty')
      : t('pagination.summary', {
          from: (current - 1) * pageSize + 1,
          to: Math.min(current * pageSize, filteredTotal),
          // Aktif kültürün rakam biçimiyle (S33-8 Pass B — binlik ayırıcı `from`/`to`'da
          // gerekmez, ikisi de tek sayfalık aralıkta kalır, yalnız `total` istendi).
          total: new Intl.NumberFormat(i18n.language).format(filteredTotal),
        });

  const btn = compact
    ? // İkon-yalnız, küçük kare — aynı kenarlık/hover/odak/disabled ailesi, yalnız boyut ve
      // yatay dolgu değişir (metin yok, `gap`/`px-3`'e gerek kalmaz).
      // `relative` + `before:` : görsel kutu 20px KALIR (satır yüksekliği bundan etkilenmez,
      // pseudo-element `absolute` yani normal akışın DIŞINDA), yalnız dokunma hedefi WCAG
      // 2.5.8 AA (24px) için `-inset-1.5` (6px) ile ~32px'e genişler. 6px, iki düğme
      // arasındaki `gap-2` (8px) sayfa göstergesine değmeden bırakılan üst sınırdır — DAHA
      // FAZLA genişletme göstergeyle çakışabilir, sınamadan artırma.
      'relative inline-flex h-5 w-5 items-center justify-center rounded-md border border-border-strong bg-surface text-fg ' +
      'before:absolute before:-inset-1.5 before:content-[""] ' +
      'hover:bg-surface-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ' +
      'disabled:cursor-not-allowed disabled:opacity-50'
    : 'inline-flex h-9 items-center gap-1 rounded-md border border-border-strong bg-surface px-3 text-sm text-fg ' +
      'hover:bg-surface-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ' +
      'disabled:cursor-not-allowed disabled:opacity-50 ' +
      // carbon: (Stage 2 "Pagination" — footer 40px kare önceki/sonraki, metin GİZLENİR
      // aşağıdaki span'da; `aria-label` her zaman verilir, bkz. altta).
      'carbon:h-10 carbon:w-10 carbon:justify-center carbon:gap-0 carbon:rounded-none carbon:border-border carbon:p-0';

  return (
    <nav
      aria-label={labels.nav}
      className={cn(
        'flex flex-wrap items-center justify-between gap-3 px-1',
        // Dikey boşluk daraltılmışta KALKAR — çağıran taraf (kart altlığı) kendi `py-2.5`'ini
        // sağlar; ikisi birden kalırsa çift dikey boşluk oluşur (bkz. `MissingItemsCard`).
        compact ? 'py-0' : 'py-2',
        // carbon: tablo altı sabit footer — 40px, üst hairline, 16px yatay dolgu.
        'carbon:h-10 carbon:border-t carbon:border-border carbon:px-4 carbon:py-0',
      )}
    >
      <p
        className={cn(compact ? 'text-xs' : 'text-sm', 'text-muted', 'carbon:text-xs')}
        aria-live="polite"
      >
        {summary}
      </p>
      {hasRows ? (
        <div className="flex items-center gap-2">
          <button
            type="button"
            className={cn(btn)}
            onClick={() => onPageChange(current - 1)}
            disabled={current <= 1}
            // Base'de yalnız `compact`ta gerekliydi (metin ADI taşıyordu); Carbon'da button
            // HER ZAMAN ikon-yalnız olduğundan (`carbon:hidden` span altta) her iki derlemede de
            // vermek zararsız — değer görünür metinle BİREBİR aynı (redundant değil, eşdeğer).
            aria-label={labels.previous}
          >
            <ChevronLeft className="h-4 w-4" aria-hidden="true" />
            {compact ? null : (
              <span className="hidden sm:inline carbon:hidden">{labels.previous}</span>
            )}
          </button>
          <span
            className={cn(
              'min-w-[3.5rem] text-center',
              compact ? 'text-xs' : 'text-sm',
              'font-medium text-fg',
              'tnum',
            )}
          >
            {current} / {pageCount}
          </span>
          <button
            type="button"
            className={cn(btn)}
            onClick={() => onPageChange(current + 1)}
            disabled={current >= pageCount}
            aria-label={labels.next}
          >
            {compact ? null : <span className="hidden sm:inline carbon:hidden">{labels.next}</span>}
            <ChevronRight className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>
      ) : null}
    </nav>
  );
}
